import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { RootLayoutContent } from '@/app/layouts/RootLayout';
import { NavigationConfig } from '@/app/types/navigation';
import {
  BillingFilters,
  PaginationParams,
} from '@/billing/api/FetchBillingList';
import { BillingList } from '@/billing/component/BillingList';
import { useFetchPaginatedBillingsSummary } from '@/billing/hooks/useFetchPaginatedBillingList';
import { BillingSortField } from '@/billing/types/SortTypes';
import { PAGE_SIZES } from '@/common/components/ui/pagination-controls';
import { usePagesNavigation } from '@/common/hooks/usePagesNavigation';

export default function BillingListPage() {
  const { t } = useTranslation();

  const [pagination, setPagination] = useState<PaginationParams>({
    pageIndex: 0,
    pageSize: PAGE_SIZES[2],
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  const [filters, setFilters] = useState<BillingFilters>({
    searchCode: '',
    accountName: '',
    policyNumber: '',
    policyType: '',
    insurer: '',
    activityCode: '',
    isAgencyBill: '',
    assignedUserId: '',
    technicalSupport: '',
    statuses: ['PENDING_VALIDATION', 'IN_PROGRESS', 'NOT_COMPLETED'],
    entity: '',
    service: '',
    gc1: '',
  });

  const {
    paginatedBillings,
    isLoading: isBillingsLoading,
    refetch,
    lastRefreshedAt,
  } = useFetchPaginatedBillingsSummary(pagination, filters);

  const handlePageChange = (newPageIndex: number) => {
    setPagination((prev) => ({ ...prev, pageIndex: newPageIndex }));
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPagination((prev) => ({ ...prev, pageIndex: 0, pageSize: newPageSize }));
  };

  const handleSort = (field: BillingSortField) => {
    setPagination((prev) => {
      const isSortingBySameField = prev.sortBy === field;

      if (!isSortingBySameField) {
        return {
          ...prev,
          sortBy: field,
          sortOrder: 'asc',
        };
      }

      return {
        ...prev,
        sortOrder: prev.sortOrder === 'asc' ? 'desc' : 'asc',
      };
    });
  };

  const handleFilterChange = (field: string, value: string | string[]) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const handleRefresh = () => {
    refetch();
  };

  const { pages, currentPage } = usePagesNavigation();

  const navigationConfig: NavigationConfig = {
    title: t('billingList'),
    backAction: { hasBackAction: false },
    pageNavigation: {
      currentPage,
      pages,
    },
  };

  return (
    <RootLayoutContent navigationConfig={navigationConfig}>
      <div className="flex flex-col h-full w-full">
        <BillingList
          billings={paginatedBillings.items}
          isLoading={isBillingsLoading}
          totalPages={paginatedBillings.totalPages}
          totalBillings={paginatedBillings.totalItems}
          currentPage={pagination.pageIndex}
          pageSize={pagination.pageSize}
          sortBy={pagination.sortBy}
          sortOrder={pagination.sortOrder}
          filters={filters}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          onSort={handleSort}
          onFilterChange={handleFilterChange}
          onRefresh={handleRefresh}
          lastRefreshedAt={lastRefreshedAt}
        />
      </div>
    </RootLayoutContent>
  );
}
